import React from "react";
import { Box, Typography, Divider, IconButton, styled, Container } from "@mui/material";
import { motion } from "framer-motion";

import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";

import imagePathImg5 from "../../assets/ico-8WxsQ4Ga.webp";
import imagePathImg6 from "../../assets/dce-DL76MlUB.webp";
import imagePathImg7 from "../../assets/bc-B_SGvi2h.webp";
import imagePathImg8 from "../../assets/cpd-D6GmoolS.webp";

// --- Styled Components ---

const FooterWrapper = styled(Box)(({ theme }) => ({
    width: "100%",
    backgroundColor: "#09122C",
    color: "#FFFFFF",
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(6),
    marginTop: theme.spacing(10),
    fontFamily: "'Inter', sans-serif",
}));

const MainGrid = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "40px",
    marginBottom: theme.spacing(8),
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        textAlign: "center",
    },
}));

const HeaderBadge = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
    [theme.breakpoints.down("md")]: {
        justifyContent: "center",
    },
}));

const NumberCircle = styled(Box)({
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "#B8860B", // Sophisticated Goldenrod
    color: "#09122C",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
    fontSize: "0.85rem",
    boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
});

const TitleLabel = styled(Typography)({
    backgroundColor: "#B8860B",
    color: "#09122C",
    padding: "4px 16px",
    borderRadius: "0 20px 20px 0",
    fontWeight: 800,
    fontSize: "0.85rem",
    textTransform: "uppercase",
    letterSpacing: "1px",
});

const PhoneBadge = styled(Box)(({ theme }) => ({
    backgroundColor: "rgba(184, 134, 11, 0.15)",
    border: "1px solid #B8860B",
    color: "#B8860B",
    padding: "6px 12px",
    marginTop: "12px",
    borderRadius: "6px",
    width: "fit-content",
    fontSize: "0.85rem",
    fontWeight: 700,
    [theme.breakpoints.down("md")]: {
        margin: "12px auto 0 auto",
    },
}));

const CertLogo = styled("img")(({ theme }) => ({
    width: 100,
    height: "auto",
    filter: "grayscale(100%) opacity(0.7)",
    transition: "all 0.4s ease",
    display: "inline-block",
    margin: "15px 25px",
    "&:hover": {
        filter: "grayscale(0%) opacity(1)",
        transform: "translateY(-5px)"
    },
    [theme.breakpoints.down("sm")]: {
        width: 80,
        margin: "10px",
    },
}));

const SocialIconBtn = styled(IconButton)(({ theme }) => ({
    color: "rgba(255,255,255,0.7)",
    display: "inline-block",
    margin: "0 12px",
    transition: "all 0.3s ease",
    "& svg": {
        fontSize: "1.8rem",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.5rem",
        },
    },
    "&:hover": {
        color: "#B8860B",
        backgroundColor: "rgba(184, 134, 11, 0.1)",
        transform: "scale(1.1)"
    }
}));

// --- Main Component ---

export default function Footer() {
    return (
        <FooterWrapper component="footer">
            <Container maxWidth="lg">
                {/* Main Content Grid */}
                <MainGrid>
                    <FooterSection
                        number="1"
                        title="About Us"
                        text="Crystal Career Centre Ltd. is registered in England and Wales (Company No. 14689000). We are British Council certified and committed to delivering top‑tier learning experiences that empower students."
                    />

                    <FooterSection
                        number="2"
                        title="Who We Are"
                        text="We are a trusted provider of educational consultancy and vocational training services. Our certified professionals deliver high‑quality programs that bridge the gap between ambition and success."
                    />

                    <FooterSection
                        number="3"
                        title="Our Mission"
                        text="Our mission is to enhance opportunities for individuals seeking career growth and specialized education. We prioritize innovation, inclusivity, and excellence in training solutions."
                    />

                    {/* 4. Contact Details */}
                    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 1 }}>
                        <HeaderBadge>
                            <NumberCircle>4</NumberCircle>
                            <TitleLabel>Contact</TitleLabel>
                        </HeaderBadge>

                        <Typography variant="body2" sx={{ lineHeight: 1.8, mb: 1, opacity: 0.9 }}>
                            <strong style={{ color: "#B8860B" }}>UK Headquarters:</strong><br />
                            19 Lavender Hill, Broughton MK10 7AA, Milton Keynes, UK.
                        </Typography>
                        <PhoneBadge>+44 7404 684200</PhoneBadge>

                        <Typography variant="body2" sx={{ lineHeight: 1.8, mt: 3, mb: 1, opacity: 0.9 }}>
                            <strong style={{ color: "#B8860B" }}>Switzerland Branch:</strong><br />
                            Neumattstrasse 9, 3127 Mühlethurnen, Switzerland.
                        </Typography>
                        <PhoneBadge>+41 76 260 1968</PhoneBadge>
                    </motion.div>


                </MainGrid>

                <Divider sx={{ backgroundColor: "rgba(255,255,255,0.1)", mb: 6 }} />


                {/* Info Text */}
                <Box sx={{ px: 3, textAlign: "center", color: "rgba(255,255,255,0.6)", maxWidth: 800, mx: "auto", mb: 8 }}>
                    <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                        We are a certified training provider with over half a decade of excellence.
                        Partnering with universities across the UK and Switzerland to bridge the global education gap.
                    </Typography>
                </Box>

                {/* Certification Logos */}
                <Box sx={{ textAlign: "center" }}>
                    <Typography variant="overline" sx={{ color: "#B8860B", fontWeight: 800, letterSpacing: 2, display: "block", mb: 2 }}>
                        Recognized & Certified By
                    </Typography>
                    <Box sx={{ display: "block" }}>
                        {[imagePathImg5, imagePathImg6, imagePathImg7, imagePathImg8].map((img, i) => (
                            <CertLogo key={i} src={img} alt="Certification Logo" />
                        ))}
                    </Box>
                </Box>


                <Box sx={{ mt: 10, pt: 4, borderTop: "1px solid rgba(255,255,255,0.05)", textAlign: "center" }}>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", display: "block", mb: 1 }}>
                        © {new Date().getFullYear()} Crystal Career Centre | All Rights Reserved
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        <a href="https://www.mavointech.fi" style={{ color: "#B8860B", textDecoration: "none" }}>
                            Powered by mavointech.fi
                        </a>
                    </Typography>
                </Box>
            </Container>
        </FooterWrapper>
    );
}

function FooterSection({ number, title, text }) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <HeaderBadge>
                <NumberCircle>{number}</NumberCircle>
                <TitleLabel>{title}</TitleLabel>
            </HeaderBadge>
            <Typography variant="body2" sx={{ lineHeight: 1.8, color: "rgba(255,255,255,0.8)" }}>
                {text}
            </Typography>
        </motion.div>
    );
}
